import { Action } from '@ngrx/store';
import { Nullable } from '@ps/utils';
import { SurveyClassification } from '@ps/survey-utils';
import { SurveyChildKey } from '../../state/survey-creation-shared-state.model';

export interface SurveyCreationCoreContext {
  readonly rootSurveyId: Nullable<number>;
  readonly activeChildKey: Nullable<SurveyChildKey>;
  readonly kind: Nullable<SurveyClassification>;
}

// Attached to every action by the wrapper in survey-creation.feature.ts,
// nested under one key so an action's own payload (e.g.
// `setActiveChildKey({ activeChildKey })`) can never shadow it the way a flat
// `activeChildKey` prop on both the enrichment and the payload used to.
//
// Extends `Action` even though `coreContextOf` never reads `.type`: without
// it, `{ coreContext?: ... }` is a "weak type" (every member optional), and
// TypeScript rejects passing a real dispatched action (which has no
// `coreContext` property of its own) to it — every reducer's `on()` handler
// call site would fail to compile. The shared `type: string` from `Action`
// is what keeps the assignment valid.
export interface ActionWithCoreContext extends Action {
  readonly coreContext?: SurveyCreationCoreContext;
}

export function withCoreContext<T extends Action>(
  action: T,
  context: Partial<SurveyCreationCoreContext> = {},
): T & ActionWithCoreContext {
  return {
    ...action,
    coreContext: {
      rootSurveyId: context.rootSurveyId ?? null,
      activeChildKey: context.activeChildKey ?? null,
      kind: context.kind ?? null,
    },
  };
}

export function coreContextOf(
  action: ActionWithCoreContext,
): SurveyCreationCoreContext {
  return {
    rootSurveyId: action.coreContext?.rootSurveyId ?? null,
    activeChildKey: action.coreContext?.activeChildKey ?? null,
    kind: action.coreContext?.kind ?? null,
  };
}
